// src/services/equityService.js
import { supabase } from '../config';
import { getAuthHeaders, handleResponse } from './utilityService';
import activityLogService from './activityLogService';

const EquityService = {
  // ===========================================
  // Optimized API Methods (commented out for now)
  // These will be implemented when optimized API services are fully integrated
  
  // async fetchOptimized(table, options = {}) {
  //   // Implementation will use optimizedAPI and cacheService
  // },

  // ===========================================
  // User Profile
  // ===========================================
  fetchUserProfile: async (userId) => {
    const response = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', userId)
      .single();
    if (response.error) throw response.error;
    return response.data;
  },

  // Alias for compatibility
  getUserProfile: async (userId) => {
    return await EquityService.fetchUserProfile(userId);
  },

  updateUserProfile: async (userId, profileData) => {
    const response = await supabase
      .from('user_profiles')
      .update(profileData)
      .eq('id', userId);
    if (response.error) throw response.error;
    return response.data;
  },

  // ===========================================
  // Companies
  // ===========================================
  fetchCompanies: async (userId) => {
    console.log('EquityService.fetchCompanies called with userId:', userId);
    if (!userId) {
      throw new Error('User ID is required for fetchCompanies');
    }
    const response = await supabase
      .from('equity_companies')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });
    if (response.error) throw response.error;
    return response.data;
  },

  createCompany: async (companyData) => {
    const response = await supabase
      .from('equity_companies')
      .insert([companyData])
      .select();
    if (response.error) throw response.error;
    
    // Log company creation
    if (response.data && response.data[0]) {
      const company = response.data[0];
      activityLogService.logEquityActivity('company_created', {
        company_id: company.id,
        company_name: company.name,
        description: company.description
      }).catch(err => console.warn('Failed to log company creation:', err));
      
      // Ensure the company owner is added to equity_users for RLS permissions
      // This is a fallback - the trigger should handle this, but we do it here too for safety
      if (company.user_id) {
        try {
          const { data: existingEquityUser } = await supabase
            .from('equity_users')
            .select('id')
            .eq('user_id', company.user_id)
            .eq('company_id', company.id)
            .maybeSingle();
          
          if (!existingEquityUser) {
            // Try to add user to equity_users (will work if RLS policy allows company owners)
            const { error: equityUserError } = await supabase
              .from('equity_users')
              .insert({
                user_id: company.user_id,
                company_id: company.id,
                role: 'creator',
                status: 'active'
              });
            
            if (equityUserError) {
              console.warn('Failed to add company owner to equity_users (this may be expected if RLS policy needs updating):', equityUserError);
              // Don't throw - the trigger or handleCompanyCreated should handle this
            } else {
              console.log('Successfully added company owner to equity_users');
            }
          }
        } catch (error) {
          console.warn('Error ensuring equity_users entry:', error);
          // Don't throw - company was created successfully
        }
      }
    }
    
    return response.data;
  },

  updateCompany: async (companyId, companyData) => {
    const response = await supabase
      .from('equity_companies')
      .update(companyData)
      .eq('id', companyId)
      .select();
    if (response.error) throw response.error;
    
    // Log company update
    if (response.data && response.data[0]) {
      activityLogService.logEquityActivity('company_updated', {
        company_id: companyId,
        updated_fields: Object.keys(companyData),
        ...companyData
      }).catch(err => console.warn('Failed to log company update:', err));
    }
    
    return response.data;
  },

  deleteCompany: async (id) => {
    // Get company details before deletion for logging
    let companyDetails = null;
    try {
      const { data } = await supabase
        .from('equity_companies')
        .select('id, name, user_id')
        .eq('id', id)
        .single();
      companyDetails = data;
    } catch (error) {
      console.warn('Could not fetch company details for logging:', error);
    }

    const response = await supabase
      .from('equity_companies')
      .delete()
      .eq('id', id);
    if (response.error) throw response.error;
    
    // Log company deletion
    if (companyDetails) {
      activityLogService.logEquityActivity('company_deleted', {
        company_id: id,
        company_name: companyDetails.name
      }).catch(err => console.warn('Failed to log company deletion:', err));
    }
    
    return response.data;
  },

  // ===========================================
  // Shareholders
  // ===========================================
  fetchShareholders: async (companyId) => {
    const response = await supabase
      .from('equity_shareholders')
      .select('*')
      .eq('company_id', companyId)
      .order('created_at', { ascending: false });
    if (response.error) throw response.error;
    return response.data;
  },

  createShareholder: async (shareholderData) => {
    const response = await supabase
      .from('equity_shareholders')
      .insert([shareholderData])
      .select();
    if (response.error) throw response.error;
    
    // Log shareholder creation
    if (response.data && response.data[0]) {
      const shareholder = response.data[0];
      activityLogService.logEquityActivity('shareholder_created', {
        shareholder_id: shareholder.id,
        company_id: shareholder.company_id,
        name: shareholder.name,
        entity_type: shareholder.entity_type,
        email: shareholder.email
      }).catch(err => console.warn('Failed to log shareholder creation:', err));
    }
    
    return response.data;
  },

  updateShareholder: async (shareholderId, shareholderData) => {
    const response = await supabase
      .from('equity_shareholders')
      .update(shareholderData)
      .eq('id', shareholderId)
      .select();
    if (response.error) throw response.error;
    
    // Log shareholder update
    if (response.data && response.data[0]) {
      const shareholder = response.data[0];
      activityLogService.logEquityActivity('shareholder_updated', {
        shareholder_id: shareholderId,
        company_id: shareholder.company_id,
        updated_fields: Object.keys(shareholderData),
        ...shareholderData
      }).catch(err => console.warn('Failed to log shareholder update:', err));
    }
    
    return response.data;
  },

  deleteShareholder: async (id) => {
    // Get shareholder details before deletion for logging
    let shareholderDetails = null;
    try {
      const { data } = await supabase
        .from('equity_shareholders')
        .select('id, name, company_id, entity_type')
        .eq('id', id)
        .single();
      shareholderDetails = data;
    } catch (error) {
      console.warn('Could not fetch shareholder details for logging:', error);
    }

    const response = await supabase
      .from('equity_shareholders')
      .delete()
      .eq('id', id);
    if (response.error) throw response.error;
    
    // Log shareholder deletion
    if (shareholderDetails) {
      activityLogService.logEquityActivity('shareholder_deleted', {
        shareholder_id: id,
        company_id: shareholderDetails.company_id,
        name: shareholderDetails.name
      }).catch(err => console.warn('Failed to log shareholder deletion:', err));
    }
    
    return response.data;
  },

  // ===========================================
  // Directors
  // ===========================================
  fetchDirectors: async (companyId) => {
    const response = await supabase
      .from('equity_directors')
      .select('*')
      .eq('company_id', companyId)
      .order('created_at', { ascending: false });
    if (response.error) throw response.error;
    return response.data;
  },

  createDirector: async (directorData) => {
    // Extract createAsShareholder flag if present (not a database column)
    const shouldCreateShareholder = directorData.createAsShareholder === true;
    const { createAsShareholder, ...directorInsertData } = directorData;
    
    // Ensure user is authenticated
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('User not authenticated');
    }
    
    // Ensure user has access in equity_users (required for RLS)
    // Check if user owns the company or has access through equity_users
    if (directorInsertData.company_id) {
      const { data: company, error: companyError } = await supabase
        .from('equity_companies')
        .select('id, user_id')
        .eq('id', directorInsertData.company_id)
        .single();
      
      console.log('Company check:', { company, companyError, userId: user.id });
      
      if (company) {
        // Check if user already has access in equity_users
        const { data: existingEquityUser, error: equityUserCheckError } = await supabase
          .from('equity_users')
          .select('id, role')
          .eq('user_id', user.id)
          .eq('company_id', directorInsertData.company_id)
          .maybeSingle();
        
        console.log('Equity user check:', { existingEquityUser, equityUserCheckError });
        
        // If user owns the company but doesn't have equity_users entry, try to add them
        if (company.user_id === user.id && !existingEquityUser) {
          console.log('User owns company but not in equity_users, attempting to add...');
          
          // Try using the assignEquityRole function which might handle RLS better
          try {
            const { assignEquityRole } = await import('./permissions/equityPermissionService');
            const result = await assignEquityRole(user.id, directorInsertData.company_id, 'creator');
            console.log('assignEquityRole result:', result);
            
            if (!result.success) {
              console.error('Failed to assign equity role:', result.error);
              // If this fails due to RLS, we need to inform the user
              if (result.error?.includes('row-level security') || result.error?.includes('permission')) {
                throw new Error('Unable to set up permissions. Please contact support or ensure you have the correct role for this company.');
              }
            }
          } catch (importError) {
            console.error('Error importing assignEquityRole:', importError);
            // Fallback: try direct insert (will likely fail but worth trying)
            const { error: equityUserError } = await supabase
              .from('equity_users')
              .insert({
                user_id: user.id,
                company_id: directorInsertData.company_id,
                role: 'creator',
                status: 'active'
              });
            
            if (equityUserError) {
              console.error('Failed to add user to equity_users:', equityUserError);
              throw new Error('You do not have permission to add directors. Please ensure you are set up as a creator for this company.');
            }
          }
        } else if (!existingEquityUser && company.user_id !== user.id) {
          // User doesn't own company and isn't in equity_users
          throw new Error('You do not have permission to add directors to this company. Please contact the company owner.');
        }
      } else if (companyError) {
        console.error('Error fetching company:', companyError);
        throw new Error('Company not found or access denied');
      }
    }
    
    console.log('Creating director with data:', directorInsertData);
    
    const response = await supabase
      .from('equity_directors')
      .insert([directorInsertData])
      .select();
    
    if (response.error) {
      console.error('Supabase error creating director:', response.error);
      console.error('Error code:', response.error.code);
      console.error('Error message:', response.error.message);
      console.error('Error details:', response.error.details);
      console.error('Error hint:', response.error.hint);
      console.error('Director data attempted:', directorInsertData);
      
      // Provide more helpful error messages
      if (response.error.code === '42501' || response.error.message?.includes('permission') || response.error.message?.includes('row-level security')) {
        throw new Error('You do not have permission to add directors to this company. Please ensure you have the correct role.');
      }
      throw response.error;
    }
    
    // Log director creation
    if (response.data && response.data[0]) {
      const director = response.data[0];
      activityLogService.logEquityActivity('director_created', {
        director_id: director.id,
        company_id: director.company_id,
        name: `${director.first_name || ''} ${director.last_name || ''}`.trim(),
        email: director.email,
        is_director: director.is_director,
        is_alternate_director: director.is_alternate_director,
        is_company_secretary: director.is_company_secretary,
        date_appointed: director.date_appointed
      }).catch(err => console.warn('Failed to log director creation:', err));
      
      // If flag is set, also create a shareholder record
      if (shouldCreateShareholder && director.company_id) {
        try {
          const shareholderName = [
            director.first_name,
            director.middle_name,
            director.last_name
          ]
            .filter(Boolean)
            .join(' ')
            .trim();
          
          const shareholderData = {
            company_id: director.company_id,
            entity_type: 'Individual',
            name: shareholderName,
            first_name: director.first_name,
            last_name: director.last_name,
            email: director.email || null,
            type: 'Individual'
          };
          
          // Clean up middle_name if empty
          if (director.middle_name && director.middle_name.trim() !== '') {
            shareholderData.middle_name = director.middle_name;
          }
          
          await EquityService.createShareholder(shareholderData);
        } catch (shareholderError) {
          console.warn('Failed to create shareholder for director:', shareholderError);
          // Don't throw - director was created successfully, shareholder creation is optional
        }
      }
    }
    
    return response.data;
  },

  updateDirector: async (directorId, directorData) => {
    const response = await supabase
      .from('equity_directors')
      .update(directorData)
      .eq('id', directorId)
      .select();
    if (response.error) throw response.error;
    
    // Log director update
    if (response.data && response.data[0]) {
      const director = response.data[0];
      activityLogService.logEquityActivity('director_updated', {
        director_id: directorId,
        company_id: director.company_id,
        updated_fields: Object.keys(directorData),
        ...directorData
      }).catch(err => console.warn('Failed to log director update:', err));
    }
    
    return response.data;
  },

  deleteDirector: async (id) => {
    // Get director details before deletion for logging
    let directorDetails = null;
    try {
      const { data } = await supabase
        .from('equity_directors')
        .select('id, company_id, first_name, last_name, email')
        .eq('id', id)
        .single();
      directorDetails = data;
    } catch (error) {
      console.warn('Could not fetch director details for logging:', error);
    }

    const response = await supabase
      .from('equity_directors')
      .delete()
      .eq('id', id);
    if (response.error) throw response.error;
    
    // Log director deletion
    if (directorDetails) {
      activityLogService.logEquityActivity('director_deleted', {
        director_id: id,
        company_id: directorDetails.company_id,
        name: `${directorDetails.first_name || ''} ${directorDetails.last_name || ''}`.trim()
      }).catch(err => console.warn('Failed to log director deletion:', err));
    }
    
    return response.data;
  },

  // ===========================================
  // Share Classes
  // ===========================================
  fetchShareClasses: async (companyId) => {
    const response = await supabase
      .from('equity_share_classes')
      .select('*')
      .eq('company_id', companyId)
      .order('priority', { ascending: true });
    if (response.error) throw response.error;
    return response.data;
  },

  createShareClass: async (shareClassData) => {
    const response = await supabase
      .from('equity_share_classes')
      .insert([shareClassData])
      .select();
    if (response.error) throw response.error;
    return response.data;
  },

  updateShareClass: async (shareClassId, shareClassData) => {
    const response = await supabase
      .from('equity_share_classes')
      .update(shareClassData)
      .eq('id', shareClassId)
      .select();
    if (response.error) throw response.error;
    return response.data;
  },

  deleteShareClass: async (id) => {
    const response = await supabase
      .from('equity_share_classes')
      .delete()
      .eq('id', id);
    if (response.error) throw response.error;
    return response.data;
  },

  // ===========================================
  // Share Issuances
  // ===========================================
  fetchIssuances: async (companyId) => {
    const response = await supabase
      .from('equity_share_issuances')
      .select(`
        *,
        shareholder:equity_shareholders!equity_share_issuances_shareholder_id_fkey(id, name, entity_type, first_name, middle_name, last_name),
        share_class:equity_share_classes!equity_share_issuances_share_class_id_fkey(id, name)
      `)
      .eq('company_id', companyId)
      .order('issue_date', { ascending: false });
    if (response.error) throw response.error;
    
    // Process the data to add shareholder_name and share_class_name fields
    return response.data.map(issuance => ({
      ...issuance,
      shareholder_name: issuance.shareholder?.name || 
                       `${issuance.shareholder?.first_name || ''} ${issuance.shareholder?.last_name || ''}`.trim() || 
                       'Unknown Shareholder',
      share_class_name: issuance.share_class?.name || 'Unknown Share Class',
      total_value: (issuance.shares || 0) * (issuance.price_per_share || 0)
    }));
  },

  createIssuance: async (issuanceData) => {
    const response = await supabase
      .from('equity_share_issuances')
      .insert([issuanceData])
      .select(`
        *,
        shareholder:equity_shareholders!equity_share_issuances_shareholder_id_fkey(*),
        share_class:equity_share_classes!equity_share_issuances_share_class_id_fkey(*)
      `);
    if (response.error) throw response.error;
    
    // Log issuance creation
    if (response.data && response.data[0]) {
      const issuance = response.data[0];
      activityLogService.logEquityActivity('issuance_created', {
        issuance_id: issuance.id,
        company_id: issuance.company_id,
        shareholder_id: issuance.shareholder_id,
        share_class_id: issuance.share_class_id,
        shares: issuance.shares,
        price_per_share: issuance.price_per_share,
        round: issuance.round,
        issue_date: issuance.issue_date
      }).catch(err => console.warn('Failed to log issuance creation:', err));
    }
    
    return response.data;
  },

  updateIssuance: async (issuanceId, issuanceData) => {
    const response = await supabase
      .from('equity_share_issuances')
      .update(issuanceData)
      .eq('id', issuanceId)
      .select(`
        *,
        shareholder:equity_shareholders!equity_share_issuances_shareholder_id_fkey(*),
        share_class:equity_share_classes!equity_share_issuances_share_class_id_fkey(*)
      `);
    if (response.error) throw response.error;
    
    // Log issuance update
    if (response.data && response.data[0]) {
      const issuance = response.data[0];
      activityLogService.logEquityActivity('issuance_updated', {
        issuance_id: issuance.id,
        company_id: issuance.company_id,
        updated_fields: Object.keys(issuanceData),
        ...issuanceData
      }).catch(err => console.warn('Failed to log issuance update:', err));
    }
    
    return response.data;
  },

  deleteIssuance: async (id) => {
    // Get issuance details before deletion for logging
    let issuanceDetails = null;
    try {
      const { data } = await supabase
        .from('equity_share_issuances')
        .select('id, company_id, shareholder_id, share_class_id, shares, round')
        .eq('id', id)
        .single();
      issuanceDetails = data;
    } catch (error) {
      console.warn('Could not fetch issuance details for logging:', error);
    }

    const response = await supabase
      .from('equity_share_issuances')
      .delete()
      .eq('id', id);
    if (response.error) throw response.error;
    
    // Log issuance deletion
    if (issuanceDetails) {
      activityLogService.logEquityActivity('issuance_deleted', {
        issuance_id: id,
        company_id: issuanceDetails.company_id,
        shareholder_id: issuanceDetails.shareholder_id,
        share_class_id: issuanceDetails.share_class_id,
        shares: issuanceDetails.shares,
        round: issuanceDetails.round
      }).catch(err => console.warn('Failed to log issuance deletion:', err));
    }
    
    return response.data;
  },

  // ===========================================
  // Share Transactions (NEW)
  // ===========================================
  fetchShareTransactions: async (companyId) => {
    const response = await supabase
      .from('equity_share_transactions')
      .select(`
        *,
        original_issuance:equity_share_issuances!equity_share_transactions_original_issuance_id_fkey(
          id, 
          shares, 
          issue_date, 
          shareholder:equity_shareholders!equity_share_issuances_shareholder_id_fkey(name, first_name, last_name), 
          share_class:equity_share_classes!equity_share_issuances_share_class_id_fkey(name)
        ),
        from_shareholder:equity_shareholders!equity_share_transactions_from_shareholder_id_fkey(id, name, first_name, last_name),
        to_shareholder:equity_shareholders!equity_share_transactions_to_shareholder_id_fkey(id, name, first_name, last_name)
      `)
      .eq('company_id', companyId)
      .order('transaction_date', { ascending: false });
    
    if (response.error) throw response.error;
    
    // Process transaction data for display
    return (response.data || []).map(transaction => ({
      ...transaction,
      from_shareholder_name: transaction.from_shareholder?.name || 
                           `${transaction.from_shareholder?.first_name || ''} ${transaction.from_shareholder?.last_name || ''}`.trim() ||
                           'Company',
      to_shareholder_name: transaction.to_shareholder?.name || 
                         `${transaction.to_shareholder?.first_name || ''} ${transaction.to_shareholder?.last_name || ''}`.trim() ||
                         'Company',
      original_shareholder_name: transaction.original_issuance?.shareholder?.name ||
                               `${transaction.original_issuance?.shareholder?.first_name || ''} ${transaction.original_issuance?.shareholder?.last_name || ''}`.trim() ||
                               'Unknown',
      share_class_name: transaction.original_issuance?.share_class?.name || 'Unknown Class',
      total_amount: (transaction.shares || 0) * (transaction.price_per_share || 0)
    }));
  },

  createShareTransaction: async (transactionData) => {
    const { data, error } = await supabase.rpc('execute_share_transaction', {
      p_transaction_data: transactionData
    });
    
    if (error) {
      console.error('RPC error creating transaction:', error);
      throw error;
    }
    
    // Check if RPC returned an error in the response
    if (data && data.error) {
      throw new Error(data.error);
    }
    
    if (data && !data.success) {
      throw new Error(data.error || 'Transaction creation failed');
    }
    
    // Log transaction creation
    if (data && data.id) {
      activityLogService.logEquityActivity('transaction_created', {
        transaction_id: data.id,
        company_id: transactionData.company_id,
        transaction_type: transactionData.transaction_type,
        original_issuance_id: transactionData.original_issuance_id,
        from_shareholder_id: transactionData.from_shareholder_id,
        to_shareholder_id: transactionData.to_shareholder_id,
        shares: transactionData.shares,
        price_per_share: transactionData.price_per_share,
        transaction_date: transactionData.transaction_date
      }).catch(err => console.warn('Failed to log transaction creation:', err));
    }
    
    return data;
  },

  updateShareTransaction: async (transactionId, transactionData) => {
    const response = await supabase
      .from('equity_share_transactions')
      .update(transactionData)
      .eq('id', transactionId)
      .select(`
        *,
        original_issuance:equity_share_issuances!equity_share_transactions_original_issuance_id_fkey(*),
        from_shareholder:equity_shareholders!equity_share_transactions_from_shareholder_id_fkey(*),
        to_shareholder:equity_shareholders!equity_share_transactions_to_shareholder_id_fkey(*)
      `);
    if (response.error) throw response.error;
    return response.data;
  },

  deleteShareTransaction: async (id) => {
    const response = await supabase
      .from('equity_share_transactions')
      .delete()
      .eq('id', id);
    if (response.error) throw response.error;
    return response.data;
  },

  // Get available shares for transaction (only active issuances)
  fetchAvailableIssuances: async (companyId) => {
    const response = await supabase
      .from('equity_share_issuances')
      .select(`
        *,
        shareholder:equity_shareholders!equity_share_issuances_shareholder_id_fkey(id, name, first_name, last_name),
        share_class:equity_share_classes!equity_share_issuances_share_class_id_fkey(id, name)
      `)
      .eq('company_id', companyId)
      .eq('status', 'active')
      .order('issue_date', { ascending: false });
    if (response.error) throw response.error;
    
    return response.data.map(issuance => ({
      ...issuance,
      shareholder_name: issuance.shareholder?.name || 
                       `${issuance.shareholder?.first_name || ''} ${issuance.shareholder?.last_name || ''}`.trim(),
      share_class_name: issuance.share_class?.name,
      display_name: `${issuance.shares} ${issuance.share_class?.name || 'shares'} - ${issuance.shareholder?.name || 'Unknown'} (${new Date(issuance.issue_date).toLocaleDateString()})`
    }));
  },

  // ===========================================
  // Capital Raising Scenarios
  // ===========================================
  fetchCapitalRaisingScenarios: async (companyId) => {
    const response = await supabase
      .from('equity_capital_raising_scenarios')
      .select('*')
      .eq('company_id', companyId)
      .order('created_at', { ascending: false });
    if (response.error) throw response.error;
    return response.data;
  },

  createCapitalRaisingScenario: async (scenarioData) => {
    const response = await supabase
      .from('equity_capital_raising_scenarios')
      .insert([scenarioData])
      .select();
    if (response.error) throw response.error;
    return response.data;
  },

  updateCapitalRaisingScenario: async (scenarioId, scenarioData) => {
    const response = await supabase
      .from('equity_capital_raising_scenarios')
      .update(scenarioData)
      .eq('id', scenarioId)
      .select();
    if (response.error) throw response.error;
    return response.data;
  },

  deleteCapitalRaisingScenario: async (id) => {
    const response = await supabase
      .from('equity_capital_raising_scenarios')
      .delete()
      .eq('id', id);
    if (response.error) throw response.error;
    return response.data;
  },

  // ===========================================
  // Equity Scenario Calculation (Python Backend)
  // ===========================================
  calculateEquityScenario: async (payload) => {
    const headers = await getAuthHeaders();
    const response = await fetch('/api/equity-calculator', {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(payload),
    });
    return handleResponse(response);
  },

  // ===========================================
  // Admin Functions
  // ===========================================
  
  // Admin API base functions
  adminApiCall: async (endpoint, options = {}) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/admin/${endpoint}`, {
      headers,
      ...options,
    });
    return handleResponse(response);
  },

  fetchAllUsers: async () => {
    return await EquityService.adminApiCall('users');
  },

  fetchAllCompanies: async () => {
    return await EquityService.adminApiCall('companies');
  },

  fetchAllShareholders: async () => {
    return await EquityService.adminApiCall('shareholders');
  },

  fetchAllDirectors: async () => {
    return await EquityService.adminApiCall('directors');
  },

  fetchAllIssuances: async () => {
    return await EquityService.adminApiCall('issuances');
  },

  fetchPlatformStats: async () => {
    return await EquityService.adminApiCall('stats');
  },

  deleteAdminRecord: async (entity, id) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/admin/${entity}/${id}`, {
      method: 'DELETE',
      headers,
    });
    return handleResponse(response);
  },

  // ===========================================
  // Import/Export Functions
  // ===========================================
  importShareholdersCSV: async (companyId, csvData) => {
    console.log('Importing shareholders CSV for company:', companyId);
    return { success: true, message: 'Import functionality to be implemented' };
  },

  importIssuancesCSV: async (companyId, csvData) => {
    console.log('Importing issuances CSV for company:', companyId);
    return { success: true, message: 'Import functionality to be implemented' };
  },

  exportCapTable: async (companyId, format = 'csv') => {
    console.log('Exporting cap table for company:', companyId, 'in format:', format);
    return { success: true, message: 'Export functionality to be implemented' };
  },

  exportIssuances: async (companyId, format = 'csv') => {
    console.log('Exporting issuances for company:', companyId, 'in format:', format);
    return { success: true, message: 'Export functionality to be implemented' };
  },

  // ===========================================
  // Utility Functions
  // ===========================================
  checkCompanyAccess: async (userId, companyId) => {
    const response = await supabase
      .from('equity_companies')
      .select('id')
      .eq('id', companyId)
      .eq('user_id', userId)
      .single();
    
    return !response.error && response.data !== null;
  },

  fetchCompanyWithDetails: async (companyId) => {
    const [company, shareholders, directors, shareClasses, issuances] = await Promise.all([
      EquityService.fetchCompanies().then(companies => companies.find(c => c.id === companyId)),
      EquityService.fetchShareholders(companyId),
      EquityService.fetchDirectors(companyId),
      EquityService.fetchShareClasses(companyId),
      EquityService.fetchIssuances(companyId),
    ]);
    return { company, shareholders, directors, shareClasses, issuances };
  },

  calculateOwnership: (issuances) => {
    const totalShares = issuances.reduce((sum, issuance) => sum + (issuance.shares || 0), 0);
    const ownershipByShareholder = issuances.reduce((acc, issuance) => {
      const shareholderId = issuance.shareholder_id;
      if (!acc[shareholderId]) {
        acc[shareholderId] = { shares: 0, percentage: 0 };
      }
      acc[shareholderId].shares += issuance.shares || 0;
      return acc;
    }, {});

    Object.keys(ownershipByShareholder).forEach(shareholderId => {
      ownershipByShareholder[shareholderId].percentage = 
        totalShares > 0
          ? ((ownershipByShareholder[shareholderId].shares / totalShares) * 100).toFixed(2)
          : 0;
    });

    return { totalShares, ownershipByShareholder };
  }
};

export default EquityService;